import {
  canAccessMerchantCompanyInformation,
  isMerchantCompanyInformationRoute,
  shouldShowUnassignedRoleGuidance,
} from './utils/merchantCompanyInformation';
import {
  hasActiveMerchantPermission,
  isActiveMerchantMemberWithoutRole,
} from './utils/merchantRbac';

/**
 * @see https://umijs.org/zh-CN/plugins/plugin-access
 * */

const isDev = process.env.NODE_ENV === 'development';
export default function access(initialState: { currentUser?: API.CurrentUser | undefined }) {
  const { currentUser } = initialState || {};
  const admins = ['partnerAdmin'];

  // 受限路由，只有渠道端可以看到
  // const limitRoute = ['/risk-control/channel-support-docs'];

  // if (conf.env === 'beta' || conf.env === 'dev') {
  //   admins.push('管理员');
  //   admins.push('beta');
  // }

  if (currentUser && (currentUser as any).forbidden) {
    return {
      canPartnerView: false,
      canAccess: false,
      canPartnerViewAndAdmin: false,
      canNotPartnerView: false,
      canPartnerFx: false,
      canSubAccount: false,
      canPartnerDirectCustomer: false,
      canRbacUnassignedRole: false,
    };
  }

  const isAdmin = currentUser && admins.filter((x) => currentUser.roles?.includes(x)).length > 0;
  const isPartnerFx = currentUser && currentUser.roles?.includes('partnerFx');
  const isPartnerDirectCustomer =
    currentUser && currentUser.roles?.includes('partnerDirectCustomer');
  const isSubAccount =
    currentUser &&
    currentUser?.merchant?.email &&
    currentUser.merchant.email !== currentUser?.email;

  // 商户 RBAC 上下文，由 app.tsx 调 /api/merchant/member/self-permission 写入。
  // 取不到（接口未部署、网络失败）或不在 RBAC 范围时，不显示成员管理入口；
  // 成员管理 API 只接受 RBAC 管理员，不能退回旧 partnerAdmin 菜单规则。
  const merchantRbac = (currentUser as any)?.merchantRbac;
  const inRbacScope = merchantRbac?.rbacScope === true;
  const rbacActive = inRbacScope && merchantRbac?.status === 'ACTIVE';

  const rbacRoutes: string[] = merchantRbac?.routes || [];

  const hasMerchantRoute = (path?: string) =>
    !!path &&
    rbacRoutes.some((grantedPath) => {
      if (grantedPath === path) return true;
      if (!grantedPath.endsWith('/*')) return false;

      const prefix = grantedPath.slice(0, -2);
      return path === prefix || path.startsWith(`${prefix}/`);
    });

  const hasMerchantPermission = (permission: string) =>
    hasActiveMerchantPermission(merchantRbac, permission);

  const isKytRoute = (path?: string) => !!path && (path === '/kyt' || path.startsWith('/kyt/'));

  const orderModuleForPath = (path?: string) => {
    if (!path) return undefined;
    const modules: [string, string][] = [
      ['/personal-transactions', 'ORDER_PERSONAL'],
      ['/batch-transfers/personal-payout-transfer', 'ORDER_PERSONAL'],
      ['/business-transactions', 'ORDER_BUSINESS'],
      ['/batch-transfers/business-payout-transfer', 'ORDER_BUSINESS'],
      ['/payroll-transactions', 'ORDER_PAYROLL'],
      ['/batch-transfers/payroll-transfer', 'ORDER_PAYROLL'],
      ['/global-payout', 'ORDER_GLOBAL'],
      ['/batch-transfers/global-transfer', 'ORDER_GLOBAL'],
      ['/global-payin', 'ORDER_GLOBAL_PAYIN'],
      // The risk-control deep link renders the personal order detail component.
      ['/risk-control/channel-order-detail', 'ORDER_PERSONAL'],
    ];
    return modules.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`))?.[1];
  };

  const canOperateCurrentOrderModule = (route?: any) => {
    const path = route?.path || (typeof window === 'undefined' ? '' : window.location.pathname);
    const module = orderModuleForPath(path);
    return !!module && hasMerchantPermission(`MERCHANT:${module}:OPERATE`);
  };

  /**
   * 范围内的路由可见性由角色 routes 决定；KYT 和全球账户引导按已确认的独立规则处理，
   * 避免同一份菜单一半走新规则、一半还看旧角色（迁移后主账号仍保留 partnerAdmin 等旧角色）。
   * WithAuthorized 的订单按钮没有 route.path 参数，因此按当前页面路径映射到对应订单模块 OPERATE；
   * 普通路由级检查仍只在确实收到 route.path 时走这套判定。
   */
  const isRouteQuery = (route: any) => inRbacScope && !!route?.path;
  const canAccessMerchantRoute = (route: any) => {
    if (!rbacActive) return false;
    const path = route?.path;

    if (isMerchantCompanyInformationRoute(path) && canAccessMerchantCompanyInformation(merchantRbac)) {
      return true;
    }

    // A roleless registration account can submit/view company materials only.
    if (isActiveMerchantMemberWithoutRole(merchantRbac)) {
      return false;
    }

    // KYT has no merchant RBAC control in this phase; every ACTIVE merchant member can see it.
    if (isKytRoute(path)) return true;

    // The guide is a feature entry point, not a standalone role route. Its caller additionally
    // checks the legacy partnerDirectCustomer feature flag; RBAC still requires wallet READ.
    if (path === '/global-account-guide') {
      return (
        hasMerchantPermission('MERCHANT:WALLET:READ') ||
        hasMerchantPermission('MERCHANT:WALLET:OPERATE')
      );
    }
    return hasMerchantRoute(path);
  };

  return {
    canPartnerView: (route: any) => {
      if (isRouteQuery(route)) {
        return canAccessMerchantRoute(route);
      }
      return (
        currentUser &&
        (currentUser.roles?.includes('partnerView') || currentUser.roles?.includes(route.path))
      );
    },
    canSubAccount: () => {
      return isSubAccount;
    },
    canAccess: (route: any) => {
      if (inRbacScope) {
        return canAccessMerchantRoute(route);
      }

      // 以下为旧逻辑，只服务 rbacScope=false 的旧商户 / 范围外账号
      const hasUserInfo = !!currentUser;
      const isSamePath = hasUserInfo && currentUser.roles?.includes(route.path);
      const hasPartnerFxRole = isPartnerFx;
      // 对于 wallets 路由，如果用户有 canPartnerDirectCustomer 权限，也应该允许访问
      // 这样可以确保有 globalAccount 权限的用户能看到 wallets 父菜单
      const canAccessWalletsWithDirectCustomer =
        route.path === '/wallets' && isPartnerDirectCustomer;
      // 对于 balance-transactions 路由，如果用户有 canPartnerDirectCustomer 权限，也应该允许访问
      // 这样可以确保有 accountTransactionList 权限的用户能看到 balanceTransactions 父菜单
      const canAccessBalanceTransactionsWithDirectCustomer =
        route.path === '/balance-transactions' && isPartnerDirectCustomer;
      // 对于 wallets/balance 路由及其子路由，如果用户有 canPartnerDirectCustomer 权限，也应该允许访问
      // 这样 canPartnerDirectCustomer 角色可以访问余额相关功能
      const canAccessBalanceWithDirectCustomer =
        route.path?.startsWith('/wallets/balance') && isPartnerDirectCustomer;
      // 对于 balance-transactions/balance 路由及其子路由，如果用户有 canPartnerDirectCustomer 权限，也应该允许访问
      // 这样 canPartnerDirectCustomer 角色可以访问余额交易列表
      const canAccessBalanceTransactionsBalanceWithDirectCustomer =
        route.path?.startsWith('/balance-transactions/balance') && isPartnerDirectCustomer;

      return (
        (hasUserInfo &&
          (isSamePath ||
            canAccessWalletsWithDirectCustomer ||
            canAccessBalanceTransactionsWithDirectCustomer ||
            canAccessBalanceWithDirectCustomer ||
            canAccessBalanceTransactionsBalanceWithDirectCustomer ||
            (hasPartnerFxRole &&
              (route.path?.startsWith('/wallets') ||
                route.path?.startsWith('/balance-transactions'))))) ||
        isAdmin
      );
    },
    canPaetnerAdmin: (route?: any) => {
      if (inRbacScope) {
        return canOperateCurrentOrderModule(route);
      }
      return isAdmin;
    },
    canPartnerTransfer: (route?: any) => {
      if (inRbacScope) {
        return canOperateCurrentOrderModule(route);
      }
      return !!currentUser?.roles?.includes('partnerTransfer');
    },
    canPartnerFx: (route?: any) => {
      if (isRouteQuery(route)) {
        return canAccessMerchantRoute(route);
      }
      return isPartnerFx;
    },

    canPartnerViewAndAdmin: (route: any) => {
      if (isRouteQuery(route)) {
        return canAccessMerchantRoute(route);
      }
      return (
        currentUser &&
        (currentUser.roles?.includes('partnerView') ||
          currentUser.roles?.includes(route.path) ||
          isAdmin)
      );
    },

    // 成员管理只对 ACTIVE 的 RBAC 商户管理员开放，与后端 MerchantAdminRequired 保持一致。
    canManageMembers: () => {
      return rbacActive && merchantRbac?.merchantAdmin === true;
    },

    canNotPartnerView: (route: any) => {
      if (isRouteQuery(route)) {
        return canAccessMerchantRoute(route);
      }
      // 新注册用户登陆 跳转到公司管理菜单时需要鉴权 防止没有权限403
      if (!currentUser || !currentUser.roles) {
        return true;
      }
      // 换汇角色不展示公司管理
      if (isPartnerFx) {
        return false;
      }
      // 子账号不展示公司管理
      if (isSubAccount && !isDev) {
        return false;
      }

      return (
        !currentUser.roles?.includes('partnerView') ||
        currentUser.roles?.includes(route.path) ||
        isAdmin
      );
    },
    canPartnerDirectCustomer: (route?: any) => {
      if (isRouteQuery(route)) {
        return !!isPartnerDirectCustomer && canAccessMerchantRoute(route);
      }
      return isPartnerDirectCustomer;
    },
    canRbacUnassignedRole: () => shouldShowUnassignedRoleGuidance(merchantRbac),
  };
}
